import { useState } from "react";
import { formatSgNumber } from "../lib/format";
import { SignupProgress } from "../lib/signupProgress";
import { SIGNOFF_HANDLE, SIGNOFF_URL } from "../lib/SignOff";
import type { SignupForm } from "./PhoneStep";

export type ApprovalReason = "uen" | "by-hand" | "number-unconfirmed";

const paynowLine = (f: SignupForm) =>
  f.proxyType === "uen" ? `UEN ${f.proxyValue.trim().toUpperCase()}` : formatSgNumber(f.proxyValue.replace(/\D/g, "").replace(/^(\d{8})$/, "65$1"));

/**
 * "We'll check it by hand": for someone who can't use WhatsApp, or when
 * confirming a number on WhatsApp isn't switched on. Asking creates the
 * organisation, held until a person approves it.
 */
export function ByHandStep({
  form,
  canTryWhatsApp,
  busy,
  error,
  onSubmit,
  onTryWhatsApp,
  onBackToDetails,
}: {
  form: SignupForm;
  canTryWhatsApp: boolean;
  busy: boolean;
  error: string;
  onSubmit: (note: string) => void;
  onTryWhatsApp: () => void;
  /** Offered when asking failed (a number that turns out to be taken). */
  onBackToDetails: () => void;
}) {
  const [note, setNote] = useState("");
  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) onSubmit(note.trim());
      }}
    >
      <SignupProgress states={["done", "done", "cur"]} third={form.proxyType === "uen" ? "Approval" : "Number"} />
      <h2 className="h1" style={{ fontSize: 30, letterSpacing: "-0.03em" }}>We’ll check it by hand</h2>
      <p className="body" style={{ margin: "12px 0 0", color: "var(--ink2)" }}>
        We’ll confirm that <b className="mono" style={{ color: "var(--ink)", fontSize: 15 }}>{paynowLine(form)}</b> is yours and approve {form.name.trim() || "your organisation"}. You’ll hear from us at <b style={{ color: "var(--ink)" }}>{form.email.trim()}</b>.
      </p>

      <div className="field" style={{ marginTop: 28 }}>
        <label className="lbl" htmlFor="bh-note">Anything that helps us check <span className="opt">(optional)</span></label>
        <textarea
          id="bh-note"
          className="pv-area"
          maxLength={600}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="For example your website or Facebook page, or who we can call."
        />
        <p className="hint">A person reads this when they check your details.</p>
      </div>

      {error && <div className="error-box" role="alert">{error}</div>}

      <div className="pv-stack">
        <button type="submit" className="btn btn--block" disabled={busy}>{busy ? "Asking…" : "Ask us to check"}</button>
        <p className="hint" style={{ margin: 0 }}>Your organisation is set up now, but its payment link and QR stay off until we approve it.</p>
      </div>
      {(canTryWhatsApp || error) && (
        <div className="pv-links">
          {canTryWhatsApp && <button type="button" className="textbtn" onClick={onTryWhatsApp}>Try WhatsApp instead</button>}
          {error && <button type="button" className="textbtn" onClick={onBackToDetails}>Back to details</button>}
        </div>
      )}
    </form>
  );
}

/** The organisation exists and is waiting for a person to approve it. */
export function AwaitingStep({
  form,
  reason,
  onDashboard,
  onSetUpEmails,
}: {
  form: SignupForm;
  reason: ApprovalReason;
  onDashboard: () => void;
  onSetUpEmails: () => void;
}) {
  const isUen = reason === "uen";
  return (
    <div>
      <SignupProgress states={["done", "done", "wait"]} third={isUen ? "Approval" : "Number"} />
      <span className="pv-tag pv-tag--wait" style={{ display: "inline-block", fontSize: 13, fontWeight: 700 }}>Waiting for approval</span>
      <h2 className="h1" style={{ fontSize: 30, letterSpacing: "-0.03em", marginTop: 12 }}>Thanks, we’re checking it</h2>

      <div className="pv-box" style={{ marginTop: 22 }}>
        <div className="pv-row"><span className="pv-k">Organisation</span><span style={{ fontWeight: 500 }}>{form.name.trim()}</span></div>
        <div className="pv-row"><span className="pv-k">PayNow</span><span className="pv-v" style={{ fontSize: 15.5 }}>{paynowLine(form)}</span></div>
        <div className="pv-row"><span className="pv-k">Contact email</span><span className="pv-v" style={{ fontSize: 15.5 }}>{form.email.trim()}</span></div>
      </div>

      <div style={{ marginTop: 22, fontSize: 16, fontWeight: 700 }}>What happens next</div>
      <ul style={{ listStyle: "none", margin: "12px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
        <li className="pv-nx"><i /><span>{isUen ? `A person checks that UEN ${form.proxyValue.trim().toUpperCase()} belongs to ${form.name.trim()}.` : `A person checks that ${paynowLine(form)} is yours.`}</span></li>
        <li className="pv-nx"><i /><span>We email {form.email.trim()} when it is approved.</span></li>
        <li className="pv-nx"><i /><span>Until then your dashboard opens, but your payment link and QR stay off, so nobody can pay a number we haven’t confirmed.</span></li>
      </ul>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "14px 20px", marginTop: 26 }}>
        <span style={{ fontSize: 14, color: "var(--ink2)" }}>
          Questions? <a className="textbtn" style={{ fontSize: 14 }} href={SIGNOFF_URL} target="_blank" rel="noopener noreferrer">Talk to {SIGNOFF_HANDLE}</a>
        </span>
        <button type="button" className="btn" onClick={onDashboard}>Go to your dashboard</button>
      </div>
      <div className="pv-links" style={{ marginTop: 18 }}>
        <button type="button" className="textbtn" onClick={onSetUpEmails}>Set up your bank emails while you wait</button>
      </div>
    </div>
  );
}
